import {
  Component,
  ContentChildren,
  QueryList,
  AfterContentInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
  OnDestroy
} from '@angular/core';
import { BsTabComponent } from './tab.component';

@Component({
  selector: 'bs-tabs',
  templateUrl: './bs-tabs.component.html',
  styleUrls: ['./bs-tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BsTabsComponent implements AfterContentInit, OnDestroy {
  @ContentChildren(BsTabComponent) tabs!: QueryList<BsTabComponent>;

  /** Optional ID prefix for the tabs (used for aria attributes) */
  @Input() idPrefix: string = 'tab';

  /** Optional CSS class for the tabs container */
  @Input() tabsClass: string = '';

  /** Optional CSS class for the tab content container */
  @Input() contentClass: string = '';

  /**
   * Enable URL hash synchronization.
   * When enabled, the active tab index is synced to the URL hash (#tab-0, #tab-1, etc.)
   * and restored when the page reloads.
   * Default: false
   */
  @Input() syncWithHash: boolean = false;

  /** The index of the currently selected tab (supports two-way binding) */
  @Input() 
  set selectedIndex(index: number) {
    if (this._selectedIndex !== index) {
      this._selectedIndex = index;
      // Update tab selection if tabs are already initialized
      if (this.tabs && this.tabs.length > 0) {
        this.selectTabByIndex(index, false);
      }
    }
  }
  
  get selectedIndex(): number {
    return this._selectedIndex;
  }
  
  private _selectedIndex: number = -1;

  /** Event emitted when the active tab changes */
  @Output() tabChange = new EventEmitter<{ 
    index: number; 
    tab: BsTabComponent;
    tabPanelId: string;
    hash: string;
  }>();

  /** Event emitted for two-way binding of selectedIndex */
  @Output() selectedIndexChange = new EventEmitter<number>();

  /** Listener for hash changes */
  private hashChangeListener: (() => void) | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterContentInit(): void {
    // Get all tabs
    const tabsList = this.tabs.toArray();

    // Check if we should restore tab from URL hash
    let initialTabIndex = -1;
    if (this.syncWithHash) {
      initialTabIndex = this.getTabIndexFromHash();
    }

    if (tabsList.length > 0) {
      // Priority 1: Hash-based tab index (if syncWithHash is enabled and valid hash exists)
      if (initialTabIndex >= 0 && initialTabIndex < tabsList.length && !tabsList[initialTabIndex].disabled) {
        this.selectTabByIndex(initialTabIndex, false);
      }
      // Priority 2: Explicit selectedIndex provided as input
      else if (this.selectedIndex >= 0 && this.selectedIndex < tabsList.length && !tabsList[this.selectedIndex].disabled) {
        this.selectTabByIndex(this.selectedIndex, false);
      } 
      // Priority 3: First active tab or first non-disabled tab
      else {
        const hasActiveTab = tabsList.some(tab => tab.active);
        if (!hasActiveTab) {
          const firstEnabledTab = tabsList.find(tab => !tab.disabled);
          if (firstEnabledTab) {
            const index = tabsList.indexOf(firstEnabledTab);
            this.selectTabByIndex(index, false);
          }
        } else {
          // Update selectedIndex to match the currently active tab
          const activeIndex = tabsList.findIndex(tab => tab.active);
          if (activeIndex !== -1) {
            this.selectedIndex = activeIndex;
            const activeTab = tabsList[activeIndex];
            // Mark lazy tab as instantiated if it's initially active
            if (activeTab.lazy && !activeTab._hasBeenInstantiated) {
              activeTab._hasBeenInstantiated = true;
            }
          }
        }
      }
    }

    // Set up hash change listener if syncWithHash is enabled
    if (this.syncWithHash) {
      this.setupHashChangeListener();
    }

    // Listen for changes in tabs (e.g., when parent component recreates tabs)
    this.tabs.changes.subscribe(() => {
      const tabsList = this.tabs.toArray();
      
      // If we have a selected index but no tab is active, reactivate it
      if (this._selectedIndex >= 0 && this._selectedIndex < tabsList.length) {
        const currentlyActiveIndex = tabsList.findIndex(tab => tab.active);
        
        // If no tab is active or a different tab is active, select the expected one
        if (currentlyActiveIndex !== this._selectedIndex) {
          const targetTab = tabsList[this._selectedIndex];
          
          // Only select if it's not disabled
          if (targetTab && !targetTab.disabled) {
            // Deactivate all tabs first
            tabsList.forEach(tab => tab.active = false);
            
            // Activate the target tab
            targetTab.active = true;
            
            // Mark lazy tab as instantiated if it should be visible
            if (targetTab.lazy && !targetTab._hasBeenInstantiated) {
              targetTab._hasBeenInstantiated = true;
            }
          }
        }
      }
      
      this.cdr.markForCheck();
    });

    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    // Clean up hash change listener
    if (this.hashChangeListener) {
      window.removeEventListener('hashchange', this.hashChangeListener);
      this.hashChangeListener = null;
    }
  }

  /**
   * Select a tab by tab component and index
   * @param selectedTab The tab to select
   * @param index The index of the tab
   * @param event The mouse event (to prevent default anchor behavior)
   */
  selectTab(selectedTab: BsTabComponent, index: number, event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    
    this.selectTabByIndex(index, true);
  }

  /**
   * Select a tab by index programmatically
   * @param index The index of the tab to select
   * @param emitEvents Whether to emit change events (default: true)
   */
  selectTabByIndex(index: number, emitEvents: boolean = true): void {
    const tabsList = this.tabs.toArray();
    
    if (index < 0 || index >= tabsList.length) {
      return;
    }

    const selectedTab = tabsList[index];
    if (selectedTab.disabled) {
      return;
    }

    // Deactivate all tabs
    tabsList.forEach(tab => {
      tab.active = false;
    });

    // Activate the selected tab
    selectedTab.active = true;
    this.selectedIndex = index;

    // Mark lazy tab as instantiated on first activation
    if (selectedTab.lazy && !selectedTab._hasBeenInstantiated) {
      selectedTab._hasBeenInstantiated = true;
    }

    // Update URL hash if sync is enabled
    if (this.syncWithHash && emitEvents) {
      this.updateHash(index);
    }

    if (emitEvents) {
      // Emit the tab change event with extended information
      this.tabChange.emit({ 
        index, 
        tab: selectedTab,
        tabPanelId: this.getTabPanelId(index),
        hash: this.getHashForIndex(index)
      });
      
      // Emit selectedIndex change for two-way binding
      this.selectedIndexChange.emit(index);
    }

    this.cdr.markForCheck();
  }

  /**
   * Get the currently selected tab index
   */
  getSelectedIndex(): number {
    const tabsList = this.tabs.toArray();
    return tabsList.findIndex(tab => tab.active);
  }

  /**
   * Get the currently selected tab component
   */
  getSelectedTab(): BsTabComponent | null {
    const tabsList = this.tabs.toArray();
    return tabsList.find(tab => tab.active) || null;
  }

  /**
   * Handle keyboard navigation for accessibility
   * Supports: Arrow keys, Home, End
   * @param event Keyboard event
   * @param currentIndex Current tab index
   */
  onKeyDown(event: KeyboardEvent, currentIndex: number): void {
    const tabsList = this.tabs.toArray();
    const enabledTabs = tabsList.filter(tab => !tab.disabled);
    const currentEnabledIndex = enabledTabs.findIndex(tab => tab === tabsList[currentIndex]);

    let targetIndex = -1;

    switch (event.key) {
      case 'ArrowLeft':
        // Move to previous enabled tab (wrap around)
        event.preventDefault();
        targetIndex = currentEnabledIndex > 0 ? currentEnabledIndex - 1 : enabledTabs.length - 1;
        break;

      case 'ArrowRight':
        // Move to next enabled tab (wrap around)
        event.preventDefault();
        targetIndex = currentEnabledIndex < enabledTabs.length - 1 ? currentEnabledIndex + 1 : 0;
        break;

      case 'Home':
        // Move to first enabled tab
        event.preventDefault();
        targetIndex = 0;
        break;

      case 'End':
        // Move to last enabled tab
        event.preventDefault();
        targetIndex = enabledTabs.length - 1;
        break;

      default:
        return;
    }

    if (targetIndex >= 0 && targetIndex < enabledTabs.length) {
      const targetTab = enabledTabs[targetIndex];
      const actualIndex = tabsList.indexOf(targetTab);
      this.selectTab(targetTab, actualIndex);

      // Focus the newly selected tab button for accessibility
      setTimeout(() => {
        const tabButton = document.getElementById(`${this.idPrefix}-${actualIndex}`);
        if (tabButton) {
          tabButton.focus();
        }
      });
    }
  }

  /**
   * Get the ID for a tab button
   * @param index Tab index
   */
  getTabId(index: number): string {
    return `${this.idPrefix}-${index}`;
  }

  /**
   * Get the ID for a tab panel
   * @param index Tab index
   */
  getTabPanelId(index: number): string {
    return `${this.idPrefix}-panel-${index}`;
  }

  /**
   * Get the href for a tab link
   * When syncWithHash is enabled, returns the actual hash path
   * Otherwise, returns the tab panel ID for accessibility
   * @param index Tab index
   */
  getTabHref(index: number): string {
    if (this.syncWithHash) {
      // Return the full current path + hash for proper navigation
      const hash = this.getHashForIndex(index);
      const currentPath = window.location.pathname + window.location.search;
      return currentPath + hash;
    }
    // Default to panel ID for accessibility (when hash sync is disabled)
    return '#' + this.getTabPanelId(index);
  }

  /**
   * Track by function for ngFor performance
   * @param index Item index
   * @param tab Tab component
   */
  trackByTab(index: number, tab: BsTabComponent): any {
    return tab.tabId || index;
  }

  /**
   * Get computed aria-label for a tab
   * @param tab Tab component
   */
  getAriaLabel(tab: BsTabComponent): string | undefined {
    if (tab.ariaLabel) {
      return tab.ariaLabel;
    }
    if (tab.badge) {
      return `${tab.label} (${tab.badge})`;
    }
    return undefined;
  }

  // ====== URL HASH SYNCHRONIZATION METHODS ======

  /**
   * Get the hash format for a tab index
   * @param index Tab index
   */
  private getHashForIndex(index: number): string {
    const tabsList = this.tabs.toArray();
    const tab = tabsList[index];
    
    // Use custom hashId if provided, otherwise fall back to idPrefix-index format
    if (tab && tab.hashId) {
      return `#${tab.hashId}`;
    }
    
    return `#${this.idPrefix}-${index}`;
  }

  /**
   * Parse the current URL hash and return the tab index
   * Returns -1 if hash doesn't match the expected format
   */
  private getTabIndexFromHash(): number {
    const hash = window.location.hash;
    if (!hash) {
      return -1;
    }

    const tabsList = this.tabs.toArray();
    
    // First, try to match by custom hashId
    const tabWithHashId = tabsList.findIndex(tab => tab.hashId && `#${tab.hashId}` === hash);
    if (tabWithHashId !== -1) {
      return tabWithHashId;
    }

    // Fall back to numeric format: #tab-0, #tab-1, etc. (using idPrefix)
    const match = hash.match(new RegExp(`^#${this.idPrefix}-(\\d+)$`));
    if (match && match[1]) {
      const index = parseInt(match[1], 10);
      return isNaN(index) ? -1 : index;
    }

    return -1;
  }

  /**
   * Update the URL hash to reflect the current tab
   * @param index Tab index
   */
  private updateHash(index: number): void {
    const newHash = this.getHashForIndex(index);
    
    // Build the complete URL with current path and new hash
    const currentPath = window.location.pathname + window.location.search;
    const newUrl = currentPath + newHash;
    
    // Update hash without triggering navigation/scroll
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', newUrl);
    } else {
      // Fallback for older browsers
      window.location.hash = newHash;
    }
  }

  /**
   * Set up listener for hash changes
   */
  private setupHashChangeListener(): void {
    this.hashChangeListener = () => {
      const index = this.getTabIndexFromHash();
      if (index >= 0 && index !== this.selectedIndex) {
        this.selectTabByIndex(index, true);
      }
    };

    window.addEventListener('hashchange', this.hashChangeListener);
  }
}
